/**
 * SEO component that queries for data with
 *  Gatsby's useStaticQuery React hook
 *
 * See: https://www.gatsbyjs.org/docs/use-static-query/
 */
import { graphql, useStaticQuery } from "gatsby";
import PropTypes from "prop-types";
import React, { useEffect } from "react";
import Helmet from "react-helmet";
import { siteUrl } from './utils/siteUrl';
import { unicodeUtil } from "./utils/unicodeUtil";
import {scriptCookies} from './utils/scriptCookies';
function SEO(props) {

  const { site, allContentfulNolPaises } = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            title
            description
            author
            siteUrl
          }
        }
        allContentfulNolPaises(filter: {node_locale: {eq: "es-CO"}}) {
          nodes {
            dominioDelPais
            codigoIcu
          }
        }
      }
    `
    );

  const meta = props.meta;
  const title = props.title;
  let lang = props.lang;
  let metaDescription = props.description || site.siteMetadata.description;
  let author = site.siteMetadata.author;
  let siteUrl = site.siteMetadata.siteUrl || siteUrl
  let slug = props.url || "/";
  let ogImageUrl = props.image || 'https://dev-portals-images.s3.amazonaws.com/NOL/preview/nosotrasonline.png';
  let script = props.script;
  let alternatesInvalidCountries = props.alternatesInvalidCountries || [];
  const finalUrl = new URL(siteUrl + slug);

  // Organization Schema
  const org = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "NosotrasOnline",
    "alternateName": author,
    "url": siteUrl,
    "logo": "https://dev-portals-images.s3.amazonaws.com/NOL/preview/nosotrasonline.png",
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": process.env.COUNTRY_CONTACT,
      "contactType": "customer service",
      "contactOption": "TollFree",
      "areaServed": "CO",
      "availableLanguage": "es"
    },
    "sameAs": "https://www.facebook.com/NosotrasColombia/"
  };

  // WebSite Schema
  const website = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": siteUrl + "#website",
    "headline": "NosotrasOnline",
    "name": "NosotrasOnline",
    "description": unicodeUtil(site.siteMetadata.description),
    "url": siteUrl,
    "potentialAction": {
      "@type": "SearchAction",
      "target": siteUrl + "?s={search_term_string}",
      "query-input": "required name=search_term_string"
    }
  };

  let schemas = [];
  if (script) {
    schemas = [org, website, script];
  } else {
    schemas = [org, website];
  }

  const schemaOrgJSONLD = {
    type: 'application/ld+json',
    innerHTML: JSON.stringify(schemas)
  };

  let hrefLangList = [];

  allContentfulNolPaises.nodes.forEach(ele => {
    let slugWithOutInitialSlash = slug.replace(/^\//, "")
    const finalUrl = new URL(ele.dominioDelPais + slugWithOutInitialSlash)
    const isInvalidCountry = alternatesInvalidCountries.find(
      country => ele.codigoIcu.toLowerCase()?.includes(country?.toLowerCase())
    )

    if (!isInvalidCountry) {
      hrefLangList.push({
        rel: "alternate",
        href: finalUrl.href,
        hrefLang: ele.codigoIcu,
      })
    }
  })

  hrefLangList = hrefLangList.concat([
    {
      rel: "alternate",
      href: `https://www.nosotrasonline.com.co${slug}`,
      hreflang: "es",
    },
    {
      rel: "alternate",
      href: `${process.env.SITE_URL}${slug}`,
      hreflang: "x-default",
    },
  ])

  const countriesPopUpScript = ['es-EC','es-AR','es-PR','es-PE','es-DO','es-BO','es-CL','es-UY']
  const SCRIPT_COOCKIES = scriptCookies[process.env.LOCALE]  || scriptCookies["es"]

  useEffect(() => {
    setTimeout(() => {
      // Crear el primer scriptCookies
      const scriptCookies1 = document.createElement('script');
      scriptCookies1.type = SCRIPT_COOCKIES[0].type;
      scriptCookies1.src = SCRIPT_COOCKIES[0].src;
      scriptCookies1.async = true;
      // Crear el segundo scriptCookies
      const scriptCookies2 = document.createElement('script');
      scriptCookies2.type = SCRIPT_COOCKIES[1].type;
      scriptCookies2.src = SCRIPT_COOCKIES[1].src;
      scriptCookies2.setAttribute('data-document-language', 'true');
      scriptCookies2.charset = 'UTF-8';
      scriptCookies2.setAttribute('data-domain-script', SCRIPT_COOCKIES[1].dataDomainScriptValue);
      scriptCookies2.async = true;
      // Agregar los scripts al final del cuerpo del documento
      document.body.appendChild(scriptCookies1);
      document.body.appendChild(scriptCookies2);
      //crear script popupSmart
      let scriptPopUpSmart
      if (countriesPopUpScript.includes(process.env.LOCALE)) {
        scriptPopUpSmart = document.createElement('script');
        scriptPopUpSmart.src = "https://cdn.popupsmart.com/bundle.js";
        scriptPopUpSmart.setAttribute("data-id", "75792")
        scriptPopUpSmart.async = true;
        document.body.appendChild(scriptPopUpSmart);
      }
      // crear script crazyEgg
      let scriptCrazyEgg
      if (process.env.LOCALE === "es-CO") {
        scriptCrazyEgg = document.createElement('script');
        scriptCrazyEgg.src = "//script.crazyegg.com/pages/scripts/0024/0375.js";
        scriptCrazyEgg.async = true;
        document.body.appendChild(scriptCrazyEgg);
      }
      // Limpiar los scripts cuando el componente se desmonta
      return () => {
        document.body.removeChild(scriptCookies1);
        document.body.removeChild(scriptCookies2);
        if (countriesPopUpScript.includes(process.env.LOCALE)) {
          document.body.removeChild(scriptPopUpSmart);
        }
        if (process.env.LOCALE === "es-CO") {
          document.body.removeChild(scriptCrazyEgg);
        }
      };
    }, 3000);
  }, []);

  return (
    <Helmet
      htmlAttributes={{ lang }}
      title={title}
      titleTemplate={`%s`}
      link={[
        {
          rel: "icon",
          type: "image/png",
          href: "/favicon.ico",
        }
      ].concat(props.alternates || hrefLangList)}
      meta={[
        {
          name: `description`,
          content: metaDescription,
        },
        {
          property: `image`,
          content: ogImageUrl,
        },
        {
          property: `fb:app_id`,
          content: `554978031914420`,
        },
        {
          property: `og:title`,
          content: title,
        },
        {
          property: `og:description`,
          content: metaDescription,
        },
        {
          property: `og:image`,
          content: ogImageUrl,
        },
        {
          property: `og:type`,
          content: `website`,
        },
        {
          property: `og:url`,
          content: finalUrl.href,
        },
        {
          name: `twitter:card`,
          content: `summary`,
        },
        {
          name: `twitter:creator`,
          content: site.siteMetadata.author,
        },
        {
          name: `twitter:title`,
          content: title,
        },
        {
          name: `twitter:description`,
          content: metaDescription,
        },
        {
          name: `twitter:image`,
          content: ogImageUrl,
        },
        {
          name: "google-site-verification",
          content: process.env.GOOGLE_SITE_VERIFICATION,
        },
      ].concat(meta)}
      script={[schemaOrgJSONLD]}
    >
      { process.env.LOCALE === 'es-CO' && (<link rel="preconnect" href="https://dev.visualwebsiteoptimizer.com" />)}
      { process.env.LOCALE === 'es-CO' && (<script type='text/javascript' id='vwoCode'>
        {`
        window._vwo_code || (function() {
        var account_id=860784,version=2.1,
        settings_tolerance=2000,
        hide_element='body',
        hide_element_style = 'opacity:0 !important;filter:alpha(opacity=0) !important;background:none !important',
        /* DO NOT EDIT BELOW THIS LINE */
        f=false,w=window,d=document,v=d.querySelector('#vwoCode'),cK='_vwo_'+account_id+'_settings',cc={};try{var c=JSON.parse(localStorage.getItem('_vwo_'+account_id+'_config'));cc=c&&typeof c==='object'?c:{}}catch(e){}var stT=cc.stT==='session'?w.sessionStorage:w.localStorage;code={use_existing_jquery:function(){return typeof use_existing_jquery!=='undefined'?use_existing_jquery:undefined},library_tolerance:function(){return typeof library_tolerance!=='undefined'?library_tolerance:undefined},settings_tolerance:function(){return cc.sT||settings_tolerance},hide_element_style:function(){return'{'+(cc.hES||hide_element_style)+'}'},hide_element:function(){if(performance.getEntriesByName('first-contentful-paint')[0]){return''}return typeof cc.hE==='string'?cc.hE:hide_element},getVersion:function(){return version},finish:function(e){if(!f){f=true;var t=d.getElementById('_vis_opt_path_hides');if(t)t.parentNode.removeChild(t);if(e)(new Image).src='https://dev.visualwebsiteoptimizer.com/ee.gif?a='+account_id+e}},finished:function(){return f},addScript:function(e){var t=d.createElement('script');t.type='text/javascript';if(e.src){t.src=e.src}else{t.text=e.text}d.getElementsByTagName('head')[0].appendChild(t)},load:function(e,t){var i=this.getSettings(),n=d.createElement('script'),r=this;t=t||{};if(i){n.textContent=i;d.getElementsByTagName('head')[0].appendChild(n);if(!w.VWO||VWO.caE){stT.removeItem(cK);r.load(e)}}else{var o=new XMLHttpRequest;o.open('GET',e,true);o.withCredentials=!t.dSC;o.responseType=t.responseType||'text';o.onload=function(){if(t.onloadCb){return t.onloadCb(o,e)}if(o.status===200){_vwo_code.addScript({text:o.responseText})}else{_vwo_code.finish('&e=loading_failure:'+e)}};o.onerror=function(){if(t.onerrorCb){return t.onerrorCb(e)}_vwo_code.finish('&e=loading_failure:'+e)};o.send()}},getSettings:function(){try{var e=stT.getItem(cK);if(!e){return}e=JSON.parse(e);if(Date.now()>e.e){stT.removeItem(cK);return}return e.s}catch(e){return}},init:function(){if(d.URL.indexOf('__vwo_disable__')>-1)return;var e=this.settings_tolerance();w._vwo_settings_timer=setTimeout(function(){_vwo_code.finish();stT.removeItem(cK)},e);var t;if(this.hide_element()!=='body'){t=d.createElement('style');var i=this.hide_element(),n=i?i+this.hide_element_style():'',r=d.getElementsByTagName('head')[0];t.setAttribute('id','_vis_opt_path_hides');v&&t.setAttribute('nonce',v.nonce);t.setAttribute('type','text/css');if(t.styleSheet)t.styleSheet.cssText=n;else t.appendChild(d.createTextNode(n));r.appendChild(t)}else{t=d.getElementsByTagName('head')[0];var n=d.createElement('div');n.style.cssText='z-index: 2147483647 !important;position: fixed !important;left: 0 !important;top: 0 !important;width: 100% !important;height: 100% !important;background: white !important;';n.setAttribute('id','_vis_opt_path_hides');n.classList.add('_vis_hide_layer');t.parentNode.insertBefore(n,t.nextSibling)}var o='https://dev.visualwebsiteoptimizer.com/j.php?a='+account_id+'&u='+encodeURIComponent(d.URL)+'&vn='+version;if(w.location.search.indexOf('_vwo_xhr')!==-1){this.addScript({src:o})}else{this.load(o+'&x=true')}}};w._vwo_code=code;code.init();})();
        `}
        
      </script>)}
    </Helmet>
  )
}

const defaultLanguage = process.env.LOCALE;
SEO.defaultProps = {
  lang: defaultLanguage || `es`,
  meta: [],
  description: ``,
}

SEO.propTypes = {
  description: PropTypes.string,
  lang: PropTypes.string,
  meta: PropTypes.arrayOf(PropTypes.object),
  title: PropTypes.string.isRequired,
}

export default SEO;


